import { useCallback, useState } from 'react';

import { useGlobalContext } from '@/context/global.context';
import useApiData from '@/hooks/useApiData';
import {
	getSolicitudDocumento as postSolicitudDocumento,
	type SolicitudesDocumentoPayload,
} from '@/services/documentacion.service';
import {
	type DocumentoContenidoPayload,
	postDocumentoContenido as postDocumentoContenidoTramite,
	postDocumentoPoderContenido,
} from '@/services/tramite.service';

import { type IMaestro } from '@/interfaces/model/maestros.interface';

type UseTramiteProps = {
	idTramite: number;
	idSolicitud?: never;
};

type UseSolicitudProps = {
	idTramite?: never;
	idSolicitud: number;
};

type UseRepresentantePoder = {
	idTramite: number | undefined;	
	idPersonaJuridica?: number;
	idRepresentante?: number;
};

type UseFetchDocumentProps = UseSolicitudProps | UseTramiteProps | UseRepresentantePoder;

type FetchConfig =
	| {
		fetch: typeof postDocumentoContenidoTramite;
		payload: DocumentoContenidoPayload;
	  }
	| {
		fetch: typeof postSolicitudDocumento;
		payload: SolicitudesDocumentoPayload;
	  }
	| {
		fetch: typeof postDocumentoPoderContenido;
		payload: UseRepresentantePoder;
	  };

/**
 * Hook para obtener el base64 del contenido de un documento.
 * @param idTramite - Se utiliza cuando el documento solicitado se encuentra en un tramite en concreto
 * @param idSolicitud - Se utiliza cuando el documento solicitado aún se encuentra en un borrador y no se ha tramitado.
 */
export const useFetchDocument = ({
	idTramite,
	// @ts-expect-error pass
	idSolicitud,
	// @ts-expect-error pass
	idPersonaJuridica,
	// @ts-expect-error pass
	idRepresentante,
}: UseFetchDocumentProps) => {
	const [data64, setData64] = useState<string | undefined>(undefined);
	const { triggerFetch, loading } = useApiData({ loading: ['onDownload'] as const });
	const { user } = useGlobalContext();

	const fetchDocument = useCallback(
		async (id: number) => {
			if (!user) return;
			let config: FetchConfig | undefined;

			const payloadCommon = {
				idDocumento: id,
				nifEmisor: user?.nifEmisor ?? '',
				tipoSector: { id: user?.tipoSectorEmisor.id, descripcion: '' } satisfies IMaestro,
			};

			if (idPersonaJuridica) {
				config = {					
					payload: {
						...payloadCommon,
						idTramite,
						idPersonaJuridica,
						idRepresentante,
					},
					fetch: postDocumentoPoderContenido,
				};
			} else if (idTramite) {
				config = {
					payload: {
						...payloadCommon,
						idTramite,
					},
					fetch: postDocumentoContenidoTramite,
				};
			} else if (idSolicitud) {
				config = {
					payload: {
						...payloadCommon,
						idSolicitud,
					},
					fetch: postSolicitudDocumento,
				};
			}

			if (!config) return;

			const response = await triggerFetch({
				fetch: config.fetch,
				payload: config.payload,
				name: 'onDownload',
				error: 'Se ha producido un error al recuperar el documento.',
			});

			if (response.data) {
				setData64(response.data);
			}

			return response;
		},
		[idTramite, idSolicitud, user?.nifEmisor, user?.tipoSectorEmisor.id],
	);

	const resetData = () => {
		setData64(undefined);
	};

	return {
		data64,
		fetchDocument,
		resetData,
		isLoading: loading.onDownload,
	};
};

export default useFetchDocument;
